import React from 'react';

import { AdContent } from 'src/app/components/Ad/AdContent';
import { AD_TYPE, CATEGORY_PREFIX, EMPTY_CATEGORY, EMPTY_PROGRAM } from 'src/app/constants/ad';
import { AdContext, AdStatusContext, filterString } from 'src/app/helpers/ad';
import { getHostname, withConfig } from 'src/app/helpers/config';
import { InjectedConfigProps } from 'src/app/models/config';

interface MyOwnProps {
  adUnitName: string;
  type: AD_TYPE;
  categoryKey?: string[];
  className?: string;
  disableBillboard?: boolean;
  programKey?: string;
  videoKey?: string;
}

type MyInjectedProps = InjectedConfigProps;

type MyProps = MyOwnProps & MyInjectedProps;

@withConfig()
class MyClassAd extends React.Component<MyProps> {
  static contextType = AdContext;
  context!: React.ContextType<typeof AdContext>;

  render(): JSX.Element | null {
    const { bottomFrameEnabled, masterheadEnabled, rectangleEnabled } = this.context;

    if ((this.props.type === AD_TYPE.BOTTOM_FRAME && !bottomFrameEnabled) ||
      (this.props.type === AD_TYPE.MASTERHEAD && !masterheadEnabled) ||
      (this.props.type === AD_TYPE.RECTANGLE && !rectangleEnabled)) {
      return null;
    }

    return (
      <AdStatusContext.Consumer>
        {(value) => value.isAdEnabled ? this.renderAds() : <div />}
      </AdStatusContext.Consumer>
    );
  }

  private get sizeMapping(): { sizes: number[][]; viewport: number[];}[] {
    const { disableBillboard, type } = this.props;
    switch (type) {
      case AD_TYPE.BOTTOM_FRAME:
        return [{ sizes: [[970, 50]], viewport: [970, 0] }, { sizes: [[320, 50]], viewport: [0, 0] }];
      case AD_TYPE.MASTERHEAD: {
        const desktopSizes = [[970, 90], [728, 90]];
        const mobileSizes = [[320, 100], [320, 50]];
        if (!disableBillboard) {
          desktopSizes.push([970, 250]);
        }
        return [{ sizes: desktopSizes, viewport: [970, 0] }, { sizes: mobileSizes, viewport: [0, 0] }];
      }
      case AD_TYPE.RECTANGLE:
      default:
        return [{ sizes: [[300, 250]], viewport: [970, 0] }];
    }
  }

  private get sizes(): number[][] {
    const { disableBillboard, type } = this.props;
    switch (type) {
      case AD_TYPE.BOTTOM_FRAME:
        return [[970, 50], [320, 50]];
      case AD_TYPE.MASTERHEAD: {
        const defaultSizes = [[970, 90], [728, 90], [320, 100], [320, 50]];
        if (!disableBillboard) {
          defaultSizes.push([970, 250]);
        }
        return defaultSizes;
      }
      case AD_TYPE.RECTANGLE:
      default:
        return [[300, 250]];
    }
  }

  private renderAds(): JSX.Element {
    const { adUnitName, categoryKey, className, config: { app: { appUrl } }, programKey, type, videoKey } = this.props;
    const category = categoryKey
      ? categoryKey.map((tag) => CATEGORY_PREFIX + filterString(tag)) : [EMPTY_CATEGORY];
    const program = programKey
      ? filterString(programKey) : EMPTY_PROGRAM;
    return (
      <AdContent
        adUnitName={adUnitName}
        category={category}
        className={className}
        hostname={getHostname(appUrl)}
        position={type}
        program={program}
        shouldRefreshAd={this.shouldRefreshAd(type)}
        sizeMapping={this.sizeMapping}
        sizes={this.sizes}
        video={videoKey}
      />
    );
  }

  private shouldRefreshAd(type: AD_TYPE) {
    return () => {
      switch (type) {
        case AD_TYPE.BOTTOM_FRAME:
        case AD_TYPE.MASTERHEAD:
          return false;
        case AD_TYPE.RECTANGLE:
        default:
          return true;
      }
    };
  }
}

export const Ad = MyClassAd as React.ComponentClass<MyOwnProps>;
